<template>
  <HKLFragment v-if="isShow">
    <slot />
  </HKLFragment>
</template>
<script>
import lodash from 'lodash'
import { computed, unref } from 'vue-demi'
import KeyMirror from 'keymirror'
import { useStoreGetters } from '@/store/index'
import { useIsProjectRepresentative } from '@/pages/projects/projectInfo'
import useRouter from '@/router/useRouter'
import { projectRouteNames, tenancyRecordsRouteNames, UserDomain } from '@/utils/consts'
import { useIsTenancyRepresentative } from '@/pages/ccd/tenancy.hook'
import HKLFragment from './HKLFragment.vue'

function isFalse (value) {
  return typeof value === 'boolean' && !value
}

function useIsRelativeRouteName (routeNames = []) {
  const { route } = useRouter()
  return computed(() => routeNames.includes(unref(route).name))
}

const MODULE_NAMES = KeyMirror({
  project: null,
  tender: null,
  tenancy: null,
})

export default {
  components: {
    HKLFragment,
  },
  props: {
    mode: {
      type: String,
      default: 'or',
      validator (value) {
        return ['and', 'or'].includes(value)
      },
    },
    not: {
      type: Boolean,
      default: false,
    },
    checkIsPIC: {
      type: [Boolean, String],
      default: false,
    },
    checkIsHKL: {
      type: Boolean,
      default: false,
    },
    checkIsVendor: {
      type: Boolean,
      default: false,
    },
    checkIsTenant: {
      type: Boolean,
      default: false,
    },
    checkIsRepresentative: {
      type: [Boolean, String],
      default: false,
      validator (value) {
        return typeof value === 'boolean' || Object.values(MODULE_NAMES).includes(value)
      },
    },
  },
  setup (props) {
    let returnData = { status: {} }
    let statusMap = {}
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const isProjectRoutePath = useIsRelativeRouteName(Object.values(projectRouteNames))
    const isTenderRoutePath = useIsRelativeRouteName(Object.values(projectRouteNames))
    const isTenancyRoutePath = useIsRelativeRouteName(Object.values(tenancyRecordsRouteNames))
    // #region PIC
    const { currentProject } = useStoreGetters('projects', ['currentProject'])
    const { currentTender } = useStoreGetters('tender', ['currentTender'])
    const isPIC = computed(() => {
      if (isFalse(props.checkIsPIC)) return false
      let picUserId
      if (typeof props.checkIsPIC === 'string') (picUserId = props.checkIsPIC)
      else if (unref(isProjectRoutePath)) (picUserId = lodash.get(unref(currentProject), 'managedBy'))
      else if (unref(isTenderRoutePath)) (picUserId = lodash.get(unref(currentTender), 'general.managedBy'))
      /** @TODO Auto check pic detail */
      return unref(currentUser).id === picUserId
    })
    statusMap = {
      ...statusMap,
      isPIC,
    }
    // #endregion PIC
    // #region HKLand
    const { isHKlandUser: isHKLandUser } = useStoreGetters('user', ['isHKlandUser'])
    statusMap = {
      ...statusMap,
      isHKLandUser,
    }
    // #endregion HKLand
    // #region Vendor
    const isVendor = computed(() => {
      /** @TODO fill up check vendor logic */
      return unref(currentUser).domain === UserDomain.CMS
    })
    statusMap = {
      ...statusMap,
      isVendor,
    }
    // #endregion Vendor
    // #region Tenant
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    statusMap = {
      ...statusMap,
      isTenant,
    }
    // #endregion Tenant
    // #region Representative
    const isProjectRepresentative = useIsProjectRepresentative()
    const isTenancyRepresentative = useIsTenancyRepresentative()
    const { isRepresentative: isTenderRepresentative } = useStoreGetters('tenders', ['isRepresentative'])
    const isRepresentative = computed(() => {
      if (isFalse(props.checkIsRepresentative)) return false
      let moduleName
      if (typeof props.checkIsRepresentative === 'string' && Object.values(MODULE_NAMES).includes(props.checkIsRepresentative)) moduleName = props.checkIsRepresentative
      else if (unref(isProjectRoutePath)) moduleName = MODULE_NAMES.project
      else if (unref(isTenderRoutePath)) moduleName = MODULE_NAMES.tender
      else if (unref(isTenancyRoutePath)) moduleName = MODULE_NAMES.tenancy
      switch (moduleName) {
        case MODULE_NAMES.project: return unref(isProjectRepresentative)
        case MODULE_NAMES.tender: return unref(isTenderRepresentative)
        case MODULE_NAMES.tenancy: return unref(isTenancyRepresentative)
        default: return false
      }
    })
    statusMap = {
      ...statusMap,
      isRepresentative,
    }
    // #endregion Representative

    const isShow = computed(() => {
      const conditions = [
        props.checkIsPIC ? unref(isPIC) : undefined,
        props.checkIsHKL ? unref(isHKLandUser) : undefined,
        props.checkIsVendor ? unref(isVendor) : undefined,
        props.checkIsTenant ? unref(isTenant) : undefined,
        props.checkIsRepresentative ? unref(isRepresentative) : undefined,
      ].filter((condition) => typeof condition === 'boolean')
      if (conditions.length === 0) return false
      let result = false
      switch (props.mode.toLowerCase()) {
        case 'and': result = conditions.every((condition) => !!condition); break
        case 'or': result = conditions.some((condition) => !!condition); break
      }
      return !props.not ? result : !result
    })
    returnData = {
      ...returnData,
      status: computed(() => lodash.mapValues(statusMap, (v) => unref(v))),
      isShow,
    }
    return returnData
  },
}
</script>
